/**
 * Reward Manager - Manages video rewards for rescue missions
 *
 * Rules:
 * - First rescue: No video
 * - Second rescue onwards: Show 15-second reward video
 * - Skip button appears after 5 seconds
 * - Game pauses completely during video playback
 */

export class RewardManager {
    constructor(game) {
        this.game = game;

        // Video configuration
        this.videoDuration = 15; // 视频总时长（秒）
        this.skipAvailableAfter = 5; // 可跳过的时间（秒）

        // State tracking
        this.rescueCount = 0; // 救援次数
        this.isVideoPlaying = false; // 是否正在播放视频
        this.videoTimer = 0; // 视频计时器（秒）
        this.canSkip = false; // 是否可以跳过

        // UI elements (will be created dynamically)
        this.videoOverlay = null;
        this.videoArea = null;
        this.countdown = null;
        this.skipButton = null;

        // Callback for video completion
        this.onVideoComplete = null;

        this.initUI();
    }

    /**
     * 初始化视频UI元素
     */
    initUI() {
        // 获取已存在的视频覆盖层（从HTML创建）
        this.videoOverlay = document.getElementById('ad-overlay');
        this.videoArea = document.getElementById('ad-video-area');
        this.countdown = document.getElementById('ad-countdown');
        this.skipButton = document.getElementById('ad-skip-button');

        if (!this.videoOverlay) {
            console.error('Video overlay element not found in HTML!');
            return;
        }

        // 绑定跳过按钮点击事件
        if (this.skipButton) {
            this.skipButton.addEventListener('click', () => this.skipVideo());
        }
    }

    /**
     * 检查是否需要显示视频
     * @returns {boolean} - 是否需要显示视频
     */
    shouldShowVideo() {
        // 第一次救援不显示视频，第二次及以后显示
        return this.rescueCount >= 1;
    }

    /**
     * 显示视频（异步）
     * @param {Function} onComplete - 视频完成或跳过后的回调函数
     * @returns {Promise<void>}
     */
    async showVideo(onComplete) {
        if (!this.shouldShowVideo()) {
            // 不需要显示视频，直接执行回调
            if (onComplete) onComplete();
            return;
        }

        this.isVideoPlaying = true;
        this.videoTimer = 0;
        this.canSkip = false;
        this.onVideoComplete = onComplete;

        // 暂停游戏
        if (this.game.pause) {
            this.game.pause();
            console.log('🎬 视频开始，游戏已暂停');
        }

        // 显示视频覆盖层
        if (this.videoOverlay) {
            this.videoOverlay.style.display = 'flex';
        }

        // 禁用跳过按钮
        if (this.skipButton) {
            this.skipButton.disabled = true;
            const t = this.game.languageManager.t.bind(this.game.languageManager);
            this.skipButton.textContent = t('reward.skipButton', { seconds: this.skipAvailableAfter });
        }

        // 开始视频倒计时
        this.startVideoTimer();
    }

    /**
     * 开始视频倒计时
     */
    startVideoTimer() {
        const updateInterval = 100; // 每100ms更新一次（更流畅的倒计时）

        const timerInterval = setInterval(() => {
            if (!this.isVideoPlaying) {
                clearInterval(timerInterval);
                return;
            }

            this.videoTimer += updateInterval / 1000; // 转换为秒

            const t = this.game.languageManager.t.bind(this.game.languageManager);

            // 更新倒计时显示
            const remainingTime = Math.max(0, this.videoDuration - this.videoTimer);
            if (this.countdown) {
                this.countdown.textContent = t('reward.countdown', { seconds: Math.ceil(remainingTime) });
            }

            // 检查是否可以跳过
            if (!this.canSkip && this.videoTimer >= this.skipAvailableAfter) {
                this.canSkip = true;
                if (this.skipButton) {
                    this.skipButton.disabled = false;
                    this.skipButton.textContent = t('reward.skipButtonReady');
                }
            }

            // 更新跳过按钮倒计时（在可跳过之前）
            if (!this.canSkip && this.skipButton) {
                const skipRemaining = Math.max(0, this.skipAvailableAfter - this.videoTimer);
                this.skipButton.textContent = t('reward.skipButton', { seconds: Math.ceil(skipRemaining) });
            }

            // 视频播放完毕
            if (this.videoTimer >= this.videoDuration) {
                clearInterval(timerInterval);
                this.completeVideo();
            }
        }, updateInterval);
    }

    /**
     * 跳过视频
     */
    skipVideo() {
        if (!this.canSkip || !this.isVideoPlaying) return;

        console.log('⏭️ 用户跳过视频');
        this.completeVideo();
    }

    /**
     * 完成视频（播放完毕或跳过）
     */
    completeVideo() {
        this.isVideoPlaying = false;

        // 隐藏视频覆盖层
        if (this.videoOverlay) {
            this.videoOverlay.style.display = 'none';
        }

        // 恢复游戏
        if (this.game.resume) {
            this.game.resume();
            console.log('▶️ 视频结束，游戏已恢复');
        }

        // 执行回调
        if (this.onVideoComplete) {
            this.onVideoComplete();
            this.onVideoComplete = null;
        }
    }

    /**
     * 记录救援次数（由RescueManager调用）
     */
    incrementRescueCount() {
        this.rescueCount++;
        console.log(`📊 救援次数: ${this.rescueCount}`);
    }

    /**
     * 重置救援次数（用于新游戏）
     */
    resetRescueCount() {
        this.rescueCount = 0;
        console.log('🔄 救援次数已重置');
    }

    /**
     * 获取当前救援次数
     */
    getRescueCount() {
        return this.rescueCount;
    }
}
